import React, { useState, useRef, useMemo } from 'react';
import { cn, formatCompact, formatNumber } from '../lib/utils';

/**
 * AreaChart — gradient-filled smooth line chart with axes and hover tooltip.
 *
 * Uses monotone-x cubic Bezier for smooth, reliable curves (no overshoot).
 *
 * props:
 *  data: [{ label, [key]: value, ... }, ...]
 *  height: number
 *  series: [{ key, label, color }]   — required
 *  formatValue: fn(value) => string   — defaults to formatCompact
 *  showGrid: bool
 *  showAxes: bool
 *  yMax: number | null              — auto-fit if null
 *  className: string
 */
export function AreaChart({
  data = [],
  height = 240,
  series,
  formatValue = formatCompact,
  showGrid = true,
  showAxes = true,
  yMax = null,
  className,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const wrapRef = useRef(null);
  const series_ = useMemo(
    () => series || [{ key: 'value', label: 'Value', color: 'hsl(var(--chart-1))' }],
    [series]
  );

  // Geometry
  const W = 1000;
  const padL = showAxes ? 44 : 8;
  const padR = showAxes ? 8 : 16;
  const padT = 16;
  const padB = showAxes ? 28 : 8;
  const innerW = W - padL - padR;
  const innerH = height - padT - padB;

  // Compute max
  const computedMax = useMemo(() => {
    if (yMax != null) return yMax;
    const m = Math.max(1, ...data.flatMap((d) => series_.map((s) => d[s.key] || 0)));
    // Round to a nice number so the top gridline feels intentional
    const mag = Math.pow(10, Math.floor(Math.log10(m)));
    return Math.ceil(m / mag) * mag;
  }, [data, series_, yMax]);

  // Map data → smoothed points (monotone-x cubic Bezier)
  const toPoints = (key) =>
    data.map((d, i) => {
      const x = padL + (i / Math.max(data.length - 1, 1)) * innerW;
      const y = padT + innerH - ((d[key] || 0) / computedMax) * innerH;
      return [x, y];
    });

  // Monotone-x cubic Bezier path (no overshoot, always smooth)
  const smoothPath = (pts) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      // Compute control points using monotone interpolation
      const t1x = p1[0] + (p2[0] - p0[0]) / 6;
      const t1y = p1[1] + (p2[1] - p0[1]) / 6;
      const t2x = p2[0] - (p3[0] - p1[0]) / 6;
      const t2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += ` C ${t1x} ${t1y}, ${t2x} ${t2y}, ${p2[0]} ${p2[1]}`;
    }
    return d;
  };

  // Y-axis ticks (5 levels)
  const yTicks = useMemo(() => {
    const count = 4;
    return Array.from({ length: count + 1 }, (_, i) => {
      const v = (computedMax * i) / count;
      const y = padT + innerH - (i / count) * innerH;
      return { v, y };
    });
  }, [computedMax, innerH, padT]);

  // X-axis stride (≈6 labels)
  const xStride = Math.max(1, Math.floor(data.length / 6));

  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-xs text-fg-muted"
        style={{ height }}
      >
        No data
      </div>
    );
  }

  const lastIdx = data.length - 1;
  const lastPoint = toPoints(series_[0].key)[lastIdx];

  return (
    <div ref={wrapRef} className={cn('w-full relative', className)}>
      <svg
        viewBox={`0 0 ${W} ${height}`}
        width="100%"
        style={{ height }}
        preserveAspectRatio="none"
        className="overflow-visible block"
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          {series_.map((s) => (
            <linearGradient
              key={`grad-${s.key}`}
              id={`areafill-${s.key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>

        {/* Grid */}
        {showGrid && yTicks.map((tick, i) => (
          <line
            key={`grid-${i}`}
            x1={padL}
            x2={padL + innerW}
            y1={tick.y}
            y2={tick.y}
            stroke="hsl(var(--border))"
            strokeDasharray={i === yTicks.length - 1 ? '0' : '2 4'}
            strokeOpacity={i === yTicks.length - 1 ? 0.7 : 0.4}
          />
        ))}

        {/* Areas + smoothed lines */}
        {series_.map((s) => {
          const pts = toPoints(s.key);
          const linePath = smoothPath(pts);
          const areaPath =
            linePath +
            ` L ${pts[pts.length - 1][0]} ${padT + innerH}` +
            ` L ${pts[0][0]} ${padT + innerH} Z`;
          return (
            <g key={`g-${s.key}`}>
              <path d={areaPath} fill={`url(#areafill-${s.key})`} />
              <path
                d={linePath}
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}

        {/* Y axis labels */}
        {showAxes && yTicks.map((tick, i) => (
          <text
            key={`ylbl-${i}`}
            x={padL - 8}
            y={tick.y + 3}
            textAnchor="end"
            fontSize="10"
            fill="hsl(var(--fg-subtle))"
            fontFamily="var(--font-euclid)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatValue(tick.v)}
          </text>
        ))}

        {/* X axis labels (strided) */}
        {showAxes && data.map((d, i) => {
          if (i % xStride !== 0 && i !== lastIdx) return null;
          const x = padL + (i / Math.max(lastIdx, 1)) * innerW;
          return (
            <text
              key={`xlbl-${i}`}
              x={x}
              y={padT + innerH + 18}
              textAnchor="middle"
              fontSize="10"
              fill="hsl(var(--fg-subtle))"
              fontFamily="var(--font-euclid)"
            >
              {d.label}
            </text>
          );
        })}

        {/* Hover crosshair + dots */}
        {hoverIdx != null && (() => {
          const xL = padL + (hoverIdx / Math.max(lastIdx, 1)) * innerW;
          return (
            <g>
              <line
                x1={xL}
                x2={xL}
                y1={padT}
                y2={padT + innerH}
                stroke="hsl(var(--fg-subtle))"
                strokeWidth="0.75"
                strokeDasharray="3 3"
                opacity="0.55"
              />
              {series_.map((s) => {
                const p = toPoints(s.key)[hoverIdx];
                if (!p) return null;
                return (
                  <circle
                    key={`dot-${s.key}`}
                    cx={p[0]}
                    cy={p[1]}
                    r="4.5"
                    fill="hsl(var(--bg))"
                    stroke={s.color}
                    strokeWidth="2"
                  />
                );
              })}
            </g>
          );
        })()}

        {/* Invisible hover targets */}
        {data.map((_, i) => {
          const x1 = padL + (i / Math.max(lastIdx, 1)) * innerW - innerW / Math.max(lastIdx, 1) / 2;
          return (
            <rect
              key={`hover-${i}`}
              x={Math.max(padL, x1)}
              y={padT}
              width={innerW / Math.max(lastIdx, 1)}
              height={innerH}
              fill="transparent"
              style={{ cursor: 'crosshair' }}
              onMouseEnter={() => setHoverIdx(i)}
              onClick={() => setHoverIdx(i)}
            />
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoverIdx != null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 px-3 py-2 rounded-lg bg-surface-2 border border-border shadow-lg text-xs whitespace-nowrap z-10"
          style={{
            left: `${(padL + (hoverIdx / Math.max(lastIdx, 1)) * innerW) / W * 100}%`,
            top: 0,
          }}
        >
          <div className="font-medium text-fg mb-1">{data[hoverIdx].label}</div>
          {series_.map((s) => (
            <div key={s.key} className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-fg-muted">{s.label}</span>
              <span className="ml-auto text-fg font-semibold tabular-nums">
                {formatNumber(data[hoverIdx][s.key] || 0)}
              </span>
            </div>
          ))}
          {/* Total row when multiple series */}
          {series_.length > 1 && (
            <div className="flex items-center justify-between mt-1 pt-1 border-t border-border">
              <span className="text-fg-muted text-2xs">Total</span>
              <span className="font-semibold tabular-nums">
                {formatNumber(
                  series_.reduce((sum, s) => sum + (data[hoverIdx][s.key] || 0), 0)
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
